import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MoreHorizontal, Eye, Edit3, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BlogEditor } from '@/components/blog-editor';
import { useCreateBlog, useUpdateBlog, useBlog, useUploadBlogImage } from '@/lib/api/hooks/use-blogs';
import type { BlogFormData } from '@/lib/api/types';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUser } from '@clerk/clerk-react';
import DOMPurify from 'dompurify';
import { PermissionGuard } from '@/components/guards/PermissionGuard';

// Helper function to strip HTML tags. DOMParser builds an inert document: unlike setting
// innerHTML on an element, it never loads images or runs onerror handlers (CNT-01).
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Helper function to generate excerpt from content
const generateExcerpt = (content: string, maxLength: number = 200): string => {
  const text = stripHtml(content).trim();
  if (text.length <= maxLength) return text;

  // Find the last complete sentence within maxLength
  const truncated = text.substring(0, maxLength);
  const lastPeriod = truncated.lastIndexOf('.');
  const lastQuestion = truncated.lastIndexOf('?');
  const lastExclamation = truncated.lastIndexOf('!');
  const lastSentenceEnd = Math.max(lastPeriod, lastQuestion, lastExclamation);

  if (lastSentenceEnd > maxLength * 0.5) {
    return text.substring(0, lastSentenceEnd + 1).trim();
  }

  // Otherwise cut at word boundary
  const lastSpace = truncated.lastIndexOf(' ');
  return text.substring(0, lastSpace).trim() + '...';
};

// Helper function to generate SEO keywords
const generateKeywords = (title: string, category: string, tags: string[]): string => {
  const keywords = new Set<string>();

  // Add category
  if (category) keywords.add(category.toLowerCase());

  // Add tags
  tags.forEach(tag => keywords.add(tag.toLowerCase()));

  // Add important words from title (excluding common words)
  const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'as'];
  const titleWords = title.toLowerCase().split(' ')
    .filter(word => word.length > 3 && !commonWords.includes(word));
  titleWords.forEach(word => keywords.add(word));

  return Array.from(keywords).join(', ');
};

export default function BlogEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const { user } = useUser();

  const { data: existingBlog, isLoading } = useBlog(id);
  const createBlog = useCreateBlog();
  const updateBlog = useUpdateBlog();
  const uploadImage = useUploadBlogImage();

  const [formData, setFormData] = useState<BlogFormData>({
    title: '',
    content: '',
    excerpt: '',
    coverImageUrl: '',
    status: 'draft',
    category: '',
    tags: [],
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
  });

  const [tagInput, setTagInput] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const coverImageInputRef = useRef<HTMLInputElement>(null);

  // Track which fields have been manually edited
  const [manuallyEdited, setManuallyEdited] = useState({
    excerpt: false,
    seoTitle: false,
    seoDescription: false,
    seoKeywords: false,
  });

  // Auto-save key for localStorage
  const autoSaveKey = `blog_autosave_${id || 'new'}`;

  // Load from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem(autoSaveKey);
    if (savedData && !existingBlog) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
        setLastSaved(new Date(parsedData.lastSaved));
        setHasUnsavedChanges(true);
      } catch (error) {
        // Failed to load auto-saved data
      }
    }
  }, [autoSaveKey, existingBlog]);

  // Load existing blog data
  useEffect(() => {
    if (existingBlog) {
      // Parse tags if they're a string (JSON)
      let tags = existingBlog.tags || [];
      if (typeof tags === 'string') {
        try {
          tags = JSON.parse(tags);
        } catch (error) {
          tags = [];
        }
      }

      setFormData({
        title: existingBlog.title,
        content: existingBlog.content,
        excerpt: existingBlog.excerpt,
        coverImageUrl: existingBlog.coverImageUrl || '',
        status: existingBlog.status,
        category: existingBlog.category || '',
        tags: Array.isArray(tags) ? tags : [],
        seoTitle: existingBlog.seoTitle || '',
        seoDescription: existingBlog.seoDescription || '',
        seoKeywords: existingBlog.seoKeywords || '',
      });
      setHasUnsavedChanges(false);
    }
  }, [existingBlog]);

  // Auto-save to localStorage every minute
  useEffect(() => {
    const interval = setInterval(() => {
      if (formData.title || formData.content) {
        const dataToSave = {
          ...formData,
          lastSaved: new Date().toISOString(),
        };
        localStorage.setItem(autoSaveKey, JSON.stringify(dataToSave));
        setLastSaved(new Date());
        setHasUnsavedChanges(true);
      }
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [formData, autoSaveKey]);

  // Auto-generate excerpt, SEO fields when content/title changes (only if not manually edited)
  useEffect(() => {
    if (formData.content && !manuallyEdited.excerpt) {
      const autoExcerpt = generateExcerpt(formData.content);
      setFormData(prev => ({ ...prev, excerpt: autoExcerpt }));
    }
  }, [formData.content, manuallyEdited.excerpt]);

  useEffect(() => {
    if (formData.title && !manuallyEdited.seoTitle) {
      setFormData(prev => ({ ...prev, seoTitle: formData.title }));
    }
  }, [formData.title, manuallyEdited.seoTitle]);

  useEffect(() => {
    if (formData.excerpt && !manuallyEdited.seoDescription) {
      const seoDesc = formData.excerpt.length > 160
        ? formData.excerpt.substring(0, 157) + '...'
        : formData.excerpt;
      setFormData(prev => ({ ...prev, seoDescription: seoDesc }));
    }
  }, [formData.excerpt, manuallyEdited.seoDescription]);

  useEffect(() => {
    if ((formData.category || formData.tags?.length) && !manuallyEdited.seoKeywords) {
      const keywords = generateKeywords(formData.title, formData.category, formData.tags || []);
      setFormData(prev => ({ ...prev, seoKeywords: keywords }));
    }
  }, [formData.category, formData.tags, formData.title, manuallyEdited.seoKeywords]);

  // Track changes for unsaved indicator
  useEffect(() => {
    if (existingBlog) {
      const hasChanges =
        formData.title !== existingBlog.title ||
        formData.content !== existingBlog.content ||
        formData.excerpt !== existingBlog.excerpt ||
        formData.coverImageUrl !== (existingBlog.coverImageUrl || '') ||
        formData.category !== (existingBlog.category || '') ||
        JSON.stringify(formData.tags) !== JSON.stringify(existingBlog.tags || []) ||
        formData.seoTitle !== (existingBlog.seoTitle || '') ||
        formData.seoDescription !== (existingBlog.seoDescription || '') ||
        formData.seoKeywords !== (existingBlog.seoKeywords || '');

      setHasUnsavedChanges(hasChanges);
    } else {
      setHasUnsavedChanges(!!(formData.title || formData.content));
    }
  }, [formData, existingBlog]);

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSubmit = async (status: 'draft' | 'published') => {
    const dataToSubmit = { ...formData, status };

    try {
      if (isEditMode && id) {
        await updateBlog.mutateAsync({ id, data: dataToSubmit });
      } else {
        await createBlog.mutateAsync(dataToSubmit);
      }

      // Clear localStorage after successful save
      localStorage.removeItem(autoSaveKey);
      setHasUnsavedChanges(false);
      setLastSaved(null);

      navigate('/blogs');
    } catch (error) {
      // Don't clear localStorage if save failed
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((t) => t !== tag) || [],
    });
  };

  const handleCoverImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }

    setIsUploadingCover(true);
    try {
      const result = await uploadImage.mutateAsync(file);
      setFormData({ ...formData, coverImageUrl: result.url });
    } catch (error) {
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploadingCover(false);
    }
  };

  if (isLoading && isEditMode) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div>
      {/* Secondary Header Bar */}
      <div className="bg-background/95 backdrop-blur-sm border-b border-border sticky top-0 z-40 -mx-4 sm:-mx-6 px-4 sm:px-6 -mt-4 sm:-mt-6 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-foreground">
              {isEditMode ? 'Edit Story' : 'New Story'}
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              {isEditMode ? 'Make changes to your blog post' : 'Write and publish your blog post'}
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {/* Auto-save indicator - hidden on mobile */}
            <div className="text-xs text-muted-foreground hidden md:block">
              {hasUnsavedChanges ? (
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                  {lastSaved ? `Last saved ${lastSaved.toLocaleTimeString()}` : 'Unsaved'}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Saved
                </span>
              )}
            </div>

            <button
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className="text-xs sm:text-sm text-muted-foreground hover:text-foreground px-2 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-accent transition-colors flex items-center gap-1 sm:gap-2"
            >
              {isPreviewMode ? (
                <>
                  <Edit3 className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Edit</span>
                </>
              ) : (
                <>
                  <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Preview</span>
                </>
              )}
            </button>
            <button
              onClick={() => handleSubmit('draft')}
              disabled={createBlog.isPending || updateBlog.isPending}
              className="text-xs sm:text-sm text-muted-foreground hover:text-foreground disabled:opacity-50 px-2 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-accent transition-colors hidden sm:block"
            >
              Save draft
            </button>
            <Sheet open={showSettings} onOpenChange={setShowSettings}>
              <SheetTrigger asChild>
                <button className="text-muted-foreground hover:text-foreground p-2 rounded-md hover:bg-accent transition-colors">
                  <MoreHorizontal className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md flex flex-col h-full">
                <SheetHeader className="flex-shrink-0">
                  <SheetTitle>Story Settings</SheetTitle>
                </SheetHeader>
                <div className="space-y-6 mt-6 overflow-y-auto flex-1 pr-2">
                  {/* Cover Image */}
                  <div className="space-y-2">
                    <Label>Cover Image</Label>
                    <div className="space-y-3">
                      <Input
                        placeholder="https://example.com/image.jpg"
                        value={formData.coverImageUrl}
                        onChange={(e) =>
                          setFormData({ ...formData, coverImageUrl: e.target.value })
                        }
                      />
                      <div className="flex items-center gap-2">
                        <div className="flex-1 border-t border-border"></div>
                        <span className="text-xs text-muted-foreground">OR</span>
                        <div className="flex-1 border-t border-border"></div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => coverImageInputRef.current?.click()}
                        disabled={isUploadingCover}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {isUploadingCover ? 'Uploading...' : 'Upload from Computer'}
                      </Button>
                      <input
                        ref={coverImageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleCoverImageUpload}
                        className="hidden"
                        disabled={isUploadingCover}
                      />
                    </div>
                    {formData.coverImageUrl && (
                      <div className="mt-2 rounded-lg overflow-hidden border border-border">
                        <img
                          src={formData.coverImageUrl}
                          alt="Cover preview"
                          className="w-full h-40 object-cover"
                        />
                      </div>
                    )}
                  </div>

                  {/* Excerpt */}
                  <div className="space-y-2">
                    <Label>Excerpt</Label>
                    <textarea
                      placeholder="Brief summary..."
                      value={formData.excerpt}
                      onChange={(e) => {
                        setFormData({ ...formData, excerpt: e.target.value });
                        setManuallyEdited(prev => ({ ...prev, excerpt: true }));
                      }}
                      className="w-full min-h-[80px] px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>

                  {/* Category */}
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Input
                      placeholder="e.g., Education"
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value })
                      }
                    />
                  </div>

                  {/* Tags */}
                  <div className="space-y-2">
                    <Label>Tags</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a tag..."
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={addTag}>
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(formData.tags || []).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-primary/10 text-primary rounded text-xs flex items-center gap-1"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="hover:text-destructive"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* SEO Section */}
                  <div className="pt-4 border-t space-y-4">
                    <h3 className="font-semibold text-sm">SEO Settings</h3>

                    <div className="space-y-2">
                      <Label>SEO Title</Label>
                      <Input
                        placeholder="Optimized title..."
                        value={formData.seoTitle}
                        onChange={(e) => {
                          setFormData({ ...formData, seoTitle: e.target.value });
                          setManuallyEdited(prev => ({ ...prev, seoTitle: true }));
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.seoTitle?.length || 0}/60 characters
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>SEO Description</Label>
                      <textarea
                        placeholder="Meta description..."
                        value={formData.seoDescription}
                        onChange={(e) => {
                          setFormData({ ...formData, seoDescription: e.target.value });
                          setManuallyEdited(prev => ({ ...prev, seoDescription: true }));
                        }}
                        className="w-full min-h-[60px] px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.seoDescription?.length || 0}/160 characters
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>SEO Keywords</Label>
                      <Input
                        placeholder="keyword1, keyword2"
                        value={formData.seoKeywords}
                        onChange={(e) => {
                          setFormData({ ...formData, seoKeywords: e.target.value });
                          setManuallyEdited(prev => ({ ...prev, seoKeywords: true }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <PermissionGuard resource="blogs" action="publish">
              <Button
                onClick={() => handleSubmit('published')}
                disabled={createBlog.isPending || updateBlog.isPending}
                className="rounded-full text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2"
              >
                Publish
              </Button>
            </PermissionGuard>
          </div>
        </div>
      </div>

      {/* Medium-style Editor/Preview */}
      <div className="mt-6 sm:mt-8">
        <div className="max-w-[740px] mx-auto px-4 sm:px-0">
          {isPreviewMode ? (
            /* Preview Mode */
            <>
              {/* Title Preview */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground mb-6 sm:mb-8">
                {formData.title || 'Untitled'}
              </h1>

              {/* Cover Image Preview */}
              {formData.coverImageUrl && (
                <div className="my-8 rounded-lg overflow-hidden">
                  <img
                    src={formData.coverImageUrl}
                    alt="Cover"
                    className="w-full h-auto"
                  />
                </div>
              )}

              {/* Content Preview */}
              <div
                className="prose prose-xl dark:prose-invert max-w-none font-serif"
                style={{ fontSize: '21px', lineHeight: '32px' }}
                // Sanitized: post content is untrusted HTML, and this page runs with an admin's session (CNT-01)
                dangerouslySetInnerHTML={{ __html: formData.content ? DOMPurify.sanitize(formData.content) : '<p class="text-muted-foreground">No content yet...</p>' }}
              />
            </>
          ) : (
            /* Edit Mode */
            <>
              {/* Title Input */}
              <textarea
                placeholder="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold border-none outline-none resize-none overflow-hidden bg-transparent placeholder:text-muted-foreground/50 text-foreground"
                style={{
                  minHeight: '48px',
                  lineHeight: '1.2',
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = target.scrollHeight + 'px';
                }}
              />

              {/* Cover Image Preview */}
              {formData.coverImageUrl && (
                <div className="my-8 rounded-lg overflow-hidden">
                  <img
                    src={formData.coverImageUrl}
                    alt="Cover"
                    className="w-full h-auto"
                  />
                </div>
              )}

              {/* Content Editor */}
              <div className="mt-8">
                <BlogEditor
                  initialContent={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                />
              </div>
            </>
          )}

          {/* Author Info and Back Button */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Written by</p>
                <p className="font-medium text-foreground">
                  {user?.firstName || user?.lastName
                    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                    : user?.primaryEmailAddress?.emailAddress?.split('@')[0] || 'Author'}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/blogs')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Blogs
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
